import { useEffect, useRef, useState } from 'react'
import { Button } from '@campgladiator/cgui-core.atoms.button'
import { useOuterClick } from '../../../../common/hooks'
import {
  analytics,
  analyticsEvent,
  analyticsEventLabel,
} from '../../../../common/utility/analytics'
import { btnSizeBreakpoint } from '../../../../common/utility/resolutionVariables'
import { FilterProps } from '../../OnDemandWorkouts.d'
import { FilterList } from '../FilterList'
import { FavoritesFilter } from '../FavoritesFilter'
import styles from './Filters.module.scss'

const { trackCustomEvent } = analytics
const { BUTTON_CLICK, CLOSE, MODAL } = analyticsEvent
const {
  WORKOUTS: { CLEAR, FILTERS, ON_DEMAND_FILTERS },
} = analyticsEventLabel

const Filters = ({
  tagFiltersApplied,
  filters,
  onOverlayIsSet,
  isLoadingFilters,
  isLoadingWorkouts,
  deviceWidth,
  isFavoriteFilterApplied,
  onGetFilteredWorkouts,
  onGetFavoriteWorkouts,
  isLoggedIn,
  isHideCompletedFilterApplied,
}: FilterProps) => {
  const numberOfFiltersApplied =
    tagFiltersApplied.length + (isHideCompletedFilterApplied ? 1 : 0)

  const filtersApplied = !!numberOfFiltersApplied

  const filterListRef = useRef<HTMLElement>(null)

  const [filtersVisible, setFiltersVisible] = useState(false)

  useEffect(() => {
    filtersVisible && trackCustomEvent(MODAL(FILTERS))
  }, [filtersVisible])

  const showFiltersListAndOverlay = (toBeVisible: boolean) => {
    setFiltersVisible(toBeVisible)
    onOverlayIsSet(toBeVisible)
  }

  const handleFiltersBtnClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    showFiltersListAndOverlay(true)
    trackCustomEvent(BUTTON_CLICK(FILTERS))
  }

  const handleClearBtnClick = (e: React.MouseEvent) => {
    onGetFilteredWorkouts([], false)
    trackCustomEvent(BUTTON_CLICK(CLEAR))
  }

  const countAllFiltersApplied = () => {
    const numOfTags = tagFiltersApplied.length
    const additionalFilters = isHideCompletedFilterApplied ? 1 : 0
    return numOfTags + additionalFilters
  }

  useOuterClick(filterListRef, () => {
    showFiltersListAndOverlay(false)
    trackCustomEvent(CLOSE(ON_DEMAND_FILTERS))
  })

  return (
    <div className={styles.content}>
      {isLoggedIn && (
        <FavoritesFilter
          className={styles.favoriteBtn}
          onOverlayIsSet={onOverlayIsSet}
          hasActiveFilters={filtersApplied}
          deviceWidth={deviceWidth}
          isLoadingWorkouts={isLoadingWorkouts}
          hasFavoriteFilter={isFavoriteFilterApplied}
          onGetFavoriteWorkouts={onGetFavoriteWorkouts}
        />
      )}
      <Button
        className={filtersApplied ? styles.activeBtn : ''}
        emphasis={filtersApplied ? 'primary' : 'secondary-alt'}
        variation="outline"
        size={deviceWidth >= btnSizeBreakpoint ? 'default' : 'small'}
        icon="icon-sliders-alt-line"
        onClick={(e) => handleFiltersBtnClick(e)}
        disabled={
          isLoadingFilters || isLoadingWorkouts || isFavoriteFilterApplied
        }
      >
        Filters {filtersApplied && ` • ${countAllFiltersApplied()}`}
      </Button>
      {filtersApplied && (
        <Button
          className={styles.clearBtn}
          size={deviceWidth >= btnSizeBreakpoint ? 'default' : 'small'}
          emphasis="secondary"
          variation="text"
          tabIndex={filtersVisible ? -1 : 0}
          onClick={handleClearBtnClick}
          disabled={isLoadingFilters || isLoadingWorkouts}
        >
          Clear
        </Button>
      )}
      {filtersVisible && (
        <FilterList
          ref={filterListRef}
          className={styles.filters}
          tagFiltersApplied={tagFiltersApplied}
          filters={filters}
          setVisible={showFiltersListAndOverlay}
          deviceWidth={deviceWidth}
          onGetFilteredWorkouts={onGetFilteredWorkouts}
          isHideCompletedFilterApplied={isHideCompletedFilterApplied}
          isLoggedIn={isLoggedIn}
        />
      )}
    </div>
  )
}

export default Filters
